import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form'; 
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { format, parse } from 'date-fns';

import { useAuth } from '../../context/AuthContext';
import api, { fetchProjectByIdApi } from '../../services/api';
import { fetchLookupDataApi } from '../../services/lookupApi';
import MetaTags from '../../components/MetaTags';

import { combinedSchema } from '../../validation/projectSchema';
import { transformToSelectOptions } from '../../utils/projectSelectUtils';

import ProjectWizard from '../../components/project/ProjectWizard';
import StepProjectDetails from '../../components/project/StepProjectDetails';
import StepShootLocations from '../../components/project/StepShootLocations';
import StepSeoMeta from '../../components/project/StepSeoMeta';
import './PostProjectsPage.css'; // Ensure this CSS is applied if needed
import { createProjectApi,updateProjectApi } from '../../services/api';

const PostProjectsPage = ({setActiveTab}) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { projectId } = useParams();

    console.log("🚀 Project ID from URL params:", projectId);

    const projectIdToEdit = projectId; 
    const [hasAdminToken, setHasAdminToken] = useState(false);
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        setHasAdminToken(!!token);
    }, []);


    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [existingImageUrl, setExistingImageUrl] = useState('');

    const [lookupOptions, setLookupOptions] = useState({
        project_type: [], program_language: [], preferred_shoot_country: [],
        address_type: [], type_of_genres: []
    });
    
    const isCastingDirector = String(user?.roleCode) === '400';

    const { watch, control, handleSubmit, reset, trigger, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(combinedSchema),
        context: { isCastingDirector, isEdit: !!projectIdToEdit },
        mode: 'onChange',
        defaultValues: {
            project_name: '', director_name: '', producer_name: '', telecast_channel: '',
            more_details:'', locations: [{ id: '', pkid: '', projectid: '', pincode: '', addressLine1: '', addressLine2: '', state: '', district: '' }],
            meta_title:'', meta_keywords:'', meta_description: ''
        }
    });

    const startDateValue = watch('start_date');
    const endDateValue = watch('end_date');
    const { fields, append, remove } = useFieldArray({ control, name: "locations" });

    const WIZARD_STEPS = [
        { id: 0, title: 'Project Details', fields: ['project_name', 'project_type', 'program_language', 'director_name', 'producer_name', 'start_date', 'end_date', 'preferred_shoot_country'] },
        { id: 1, title: 'Shoot Locations', fields: ['locations.0.addressLine1', 'locations.0.state', 'locations.0.district', 'locations.0.pincode'] },
        // { id: 2, title: 'SEO & Meta', fields: ['meta_title', 'meta_keywords', 'meta_description'] }
    ];
        const adminSteps = hasAdminToken
        ? [{ id: 2, title: 'SEO & Meta', fields: ['meta_title', 'meta_keywords', 'meta_description'] }]
        : [];
 
        const baseWizardSteps = [...WIZARD_STEPS, ...adminSteps];      
        
    const handlePincodeLookup = async (pincode, index) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            // const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            let response;
            try {
                // 🔥 FIRST API
                response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);

                // 🔥 IF FAILED THEN BACKUP API
                if (!response.data?.[0] || response.data?.[0]?.Status !== "Success") {
                    response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                    
                    // 🔥 BACKUP RESPONSE FORMAT FIX
                    response.data = [{
                        Status: response.data.Status,
                        PostOffice: response.data.PostOffice
                    }];
                }
            } catch {
                // 🔥 DIRECT BACKUP API
                response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                
                response.data = [{
                    Status: response.data.Status,
                    PostOffice: response.data.PostOffice
                }];
            }
            const data = response.data;
            if (data && data.length > 0 && data[0].Status === 'Success') {
                const { State, District } = data[0].PostOffice[0];
                setValue(`locations.${index}.state`, State, { shouldValidate: true });
                setValue(`locations.${index}.district`, District, { shouldValidate: true });
            } else {
                setApiMessage({ type: 'warning', text: `Pincode not found for location #${index + 1}` });
            }
        } catch (error) {
            console.error("Pincode API Error:", error);
        }
    };

    const loadData = useCallback(async () => {
        setPageLoading(true);
        try {
            const groups = {
                project_type: fetchLookupDataApi('projectType'),
                program_language: fetchLookupDataApi('Languages'),
                preferred_shoot_country: fetchLookupDataApi('countryOfResidence'),
                address_type: fetchLookupDataApi('addressType'),
                type_of_genres: fetchLookupDataApi('typeOfGenres'),
            };
            const responses = await Promise.all(Object.values(groups));
            console.log("responses----",responses);
            const [pType, pLang, pCountry, pAddr, pGenre] = responses;

            const loadedLookups = {
                project_type: transformToSelectOptions(pType.data.data, 'lookupValue', 'lookupLabel'),
                program_language: transformToSelectOptions(pLang.data.data, 'lookupValue', 'lookupLabel'),
                preferred_shoot_country: transformToSelectOptions(pCountry.data.data, 'lookupValue', 'lookupLabel'),
                address_type: transformToSelectOptions(pAddr.data.data, 'lookupValue', 'lookupLabel'),
                type_of_genres: transformToSelectOptions(pGenre.data.data, 'lookupValue', 'lookupLabel')
            };
            setLookupOptions(loadedLookups);

            if (projectIdToEdit) {
                const response = await fetchProjectByIdApi(projectIdToEdit);
                const raw = response.data;

                if (raw) {
                    if (raw.projectImageUrl) setExistingImageUrl(raw.projectImageUrl);

                    let parsedLocations = [];
                    try { 
                        parsedLocations = typeof raw.shoot_locations_json === 'string' 
                            ? JSON.parse(raw.shoot_locations_json) 
                            : (raw.projectShootLocationJson || []); 
                    } 
                    catch (e) { 
                        console.warn("Failed to parse shoot_locations_json, using default.", e);
                        parsedLocations = [{ id: '', pincode: '', addressLine1: '', state: '', district: '' }]; 
                    }

                    const mapLookup = (rawVal, lookups) => (rawVal ? rawVal.split(';').map(v => lookups.find(o => o.value === v)).filter(Boolean) : []);

                    reset({
                        project_name: raw.projectName || raw.project_name || '',
                        director_name: raw.projectDirectorName || raw.director_name || '',
                        producer_name: raw.projectProduceName || raw.producer_name || '',
                        telecast_channel: raw.projectTelecastOnChannels || raw.telecast_channel || '', 
                        more_details: raw.projectMoreDetail || raw.more_details || '',
                        start_date: raw.projectStartDate ? parse(raw.projectStartDate, 'yyyy-MM-dd', new Date()) : (raw.start_date ? parse(raw.start_date, 'yyyy-MM-dd', new Date()) : null),
                        end_date: raw.projectEndDate ? parse(raw.projectEndDate, 'yyyy-MM-dd', new Date()) : (raw.end_date ? parse(raw.end_date, 'yyyy-MM-dd', new Date()) : null),
                        // Here, project_type needs to be the full object, not just a value
                        project_type: loadedLookups.project_type.find(o => o.value === (raw.projectType || raw.project_type)) || null,
                        program_language: mapLookup(raw.programLanguage || raw.program_language, loadedLookups.program_language),
                        preferred_shoot_country: mapLookup(raw.shootLocationCountry || raw.shoot_location_country, loadedLookups.preferred_shoot_country), 
                        type_of_genres: mapLookup(raw.typeOfGenres || raw.type_of_genres, loadedLookups.type_of_genres),
                        locations: parsedLocations.length > 0 ? parsedLocations : [{ pincode: '', addressLine1: '', state: '', district: '' }],
                        meta_title: raw.metaTitle || raw.meta_title || '',
                        meta_keywords: raw.metaKeywords || raw.meta_keywords || '',
                        meta_description: raw.metaDescription || raw.meta_description || ''
                    });
                }
            }
        } catch (err) { 
            console.error("Data Load Error:", err); 
            setApiMessage({ type: 'danger', text: 'Failed to load project details.' });
        } finally { setPageLoading(false); }
    }, [projectIdToEdit, reset]);

    useEffect(() => { 
        loadData(); 
        return () => {
            reset(); 
        };
    }, [loadData, projectIdToEdit, reset]);
     const handleStepClick = async (targetStep) => {
        if (targetStep === currentStep) return;

        // 1. Agar PEECHE ja rahe hain to bina validation ke jaane do
        if (targetStep < currentStep) {
            setApiMessage({ type: '', text: '' });
            setCurrentStep(targetStep);
            window.scrollTo(0, 0);
            return;
        }

        // 2. Agar AAGE ja rahe hain to CURRENT STEP validate karo
        const fieldsToValidate = baseWizardSteps[currentStep]?.fields || [];
        const isValid = await trigger(fieldsToValidate);

        if (isValid) {
            setApiMessage({ type: '', text: '' });
            setCurrentStep(targetStep);
            window.scrollTo(0, 0);
        } else {
            const fieldLabels = { 
                project_name: "Project Name", project_type: "Project Type", 
                program_language: "Language", director_name: "Director Name", 
                producer_name: "Producer Name", start_date: "Start Date", 
                end_date: "End Date", preferred_shoot_country: "Shoot Country",
                'locations.0.addressLine1': "Location Address", 
                'locations.0.state': "Location State", 
                'locations.0.district': "Location District", 
                'locations.0.pincode': "Location Pincode",
                meta_title: "Meta Title", meta_keywords: "Meta Keywords",
                meta_description: "Meta Description"
            };
            const missing = fieldsToValidate.filter(field => {
                if (errors[field]) return true;
                if (field.includes('locations')) {
                    const [arrayName, index, propName] = field.split('.');
                    return errors[arrayName]?.[index]?.[propName];
                }
                return false;
            }).map(field => fieldLabels[field] || field).join(', ');

            setApiMessage({ 
                type: 'danger', 
                text: `Please complete the required fields in the current step first: ${missing || 'Highlighted fields'}` 
            });
            window.scrollTo(0, 0);
        }
    };

    const handleNext = async () => {
        // const fieldsToValidate = WIZARD_STEPS[currentStep].fields;
        const fieldsToValidate = baseWizardSteps[currentStep]?.fields || [];
        const isValid = await trigger(fieldsToValidate);
        
        if (isValid) {
            setApiMessage({ type: '', text: '' });
            // if (currentStep < WIZARD_STEPS.length - 1) {
            if (currentStep < baseWizardSteps.length - 1) {
                setCurrentStep(prev => prev + 1);
                window.scrollTo(0, 0);
            } else {
                handleSubmit(onSubmit)();
            }
        } else {
             const fieldLabels = { 
                project_name: "Project Name", project_type: "Project Type", 
                program_language: "Language", director_name: "Director Name", 
                producer_name: "Producer Name", start_date: "Start Date", 
                end_date: "End Date", preferred_shoot_country: "Shoot Country",
                'locations.0.addressLine1': "Location Address", 
                'locations.0.state': "Location State", 
                'locations.0.district': "Location District", 
                'locations.0.pincode': "Location Pincode",
                meta_title: "Meta Title", meta_keywords: "Meta Keywords",
                meta_description: "Meta Description"

            };
             const missing = fieldsToValidate.filter(field => {
                 if (errors[field]) return true;
                 if (field.includes('locations')) {
                    const [arrayName, index, propName] = field.split('.');
                    return errors[arrayName]?.[index]?.[propName];
                 }
                 return false;
             }).map(field => fieldLabels[field] || field).join(', ');
            setApiMessage({ type: 'danger', text: `Validation Failed. Please check the following fields: ${missing || 'Highlighted fields above'}` });
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => { if (currentStep > 0) { setCurrentStep(prev => prev - 1); window.scrollTo(0, 0); } };

    const onSubmit = async (data) => {
         console.log("Final Data:", data);
    console.log("File in data:", data.synopsis_upload);
        setLoading(true);
        setApiMessage({ type: '', text: '' }); 
        try {
            const formData = new FormData();
            formData.append('project_name', data.project_name);
            formData.append('director_name', data.director_name);
            formData.append('producer_name', data.producer_name);
            formData.append('start_date', data.start_date ? format(data.start_date, 'yyyy-MM-dd') : '');
            formData.append('end_date', data.end_date ? format(data.end_date, 'yyyy-MM-dd') : '');
            formData.append('project_type', data.project_type?.value || ''); // ✅ Ensure only value is sent for single select
            formData.append('program_language', data.program_language?.map(g => g.value).join(';') || ''); 
            formData.append('preferred_shoot_country', data.preferred_shoot_country?.map(g => g.value).join(';') || ''); 
            formData.append('type_of_genres', data.type_of_genres?.map(g => g.value).join(';') || ''); 
            formData.append('more_details', data.more_details || '');
            formData.append('shoot_locations_json', JSON.stringify(data.locations));
            formData.append('meta_title', data.meta_title || '');
            formData.append('meta_keywords', data.meta_keywords || '');
            formData.append('meta_description', data.meta_description || '');
            if (data.synopsis_upload?.[0]) {
                formData.append('projectimage', data.synopsis_upload[0]);
            } else if (existingImageUrl && projectIdToEdit) {
                // Keep existing image if no new one is uploaded
                // Backend usually handles this if image field is not present in multipart
                // or you might send a flag like 'keep_existing_image': 'true'
            }

            if (projectIdToEdit) {
                formData.append('projectId', projectIdToEdit);
                // await api.post('/projects/update', formData);
                await updateProjectApi(formData);
                setApiMessage({ type: 'success', text: 'Project updated successfully!' });
            } else {
                // await api.post('/projects/create', formData);
                await createProjectApi(formData);
                setApiMessage({ type: 'success', text: 'Project created successfully!' });
            }
            setTimeout(() => {
                if (setActiveTab){
                    setActiveTab("projects");
                } 
                else {
                    if(hasAdminToken){
                        navigate("/admin/projects");
                    }else{
                        navigate("/dashboard/projects");
                    }
                }
            }, 1500);

            // setTimeout(() => {
            //     navigate('/dashboard/projects');
            // }, 1500); 
            
        } catch (err) { 
            console.error("Submission Error:", err);
            const errorMessage = err.response?.data?.message || 'Operation Failed';
            setApiMessage({ type: 'danger', text: errorMessage }); 
        } finally { setLoading(false); }
    };

    if (pageLoading) return <div className="text-center text-white p-5"><Spinner animation="border" variant="danger" /> Loading Form...</div>;

    return (
        <div className="post-project-page project-mobile-fix">
            <MetaTags title={projectIdToEdit ? 'Edit Project' : 'Post New Project'} />
            
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="text-uppercase fw-bold text-white mb-0">
                    <span className="text-danger border-start border-4 border-danger ps-3">{projectIdToEdit ? 'Edit Project' : 'Post New Project'}</span>
                </h4>
            </div>

            {/* <ProjectWizard currentStep={currentStep} steps={WIZARD_STEPS} /> */}
            <ProjectWizard currentStep={currentStep} steps={baseWizardSteps} onStepClick={handleStepClick} />

            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({ type: '', text: '' })}>{apiMessage.text}</Alert>}
            
            <div className="custom-card-box">
                <Form noValidate>
                    {currentStep === 0 && <StepProjectDetails control={control} errors={errors} lookupOptions={lookupOptions} existingImageUrl={existingImageUrl} startDateValue={startDateValue} endDateValue={endDateValue} />}
                    {currentStep === 1 && <StepShootLocations control={control} errors={errors} fields={fields} append={append} remove={remove} handlePincodeLookup={handlePincodeLookup} projectIdToEdit={projectIdToEdit} />}
                    {currentStep === 2 && hasAdminToken && <StepSeoMeta control={control} errors={errors} />}
                    {/* {currentStep === 2 && <StepSeoMeta control={control} errors={errors} />} */}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4 mb-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light shadow-sm">
                    <FaChevronLeft className="me-2" /> Back
                </Button>
                <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>
                    {/* {loading ? <Spinner animation="border" size="sm" className='text-white'/> : (currentStep === WIZARD_STEPS.length - 1 ? 'FINALIZE & SAVE' : <>Next <FaChevronRight className="ms-2" /></>)} */}
                    {loading ? <Spinner animation="border" size="sm" className='text-white'/> : (currentStep === baseWizardSteps.length - 1 ? 'FINALIZE & SAVE' : <>Next <FaChevronRight className="ms-2" /></>)}

                </Button>
            </div>
        </div>
    );
};

export default PostProjectsPage;